import { CustomerImpact } from '@canvas/operational-types';

import { SiteEdition, type Site } from './sites-types.ts';

const urlStringFromHostname = (hostname: string) => `https://${hostname}`;

/**
 * ⚠️ IMPORTANT ⚠️
 * If you are adding another site here, ensure that it is added to the appropriate exclusion list
 * in the below statsig tenant lists. This is because we frequently face in-product-messaging issues where
 * a big banner blocks the entire page, breaking our pollinators and resulting in noise:
 * - Staging tenants: https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/segments/segment%3Aconfluence_whiteboards_pollinator_tenants_staging
 * - Prod tenants: https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/segments/segment%3Aconfluence_whiteboards_pollinator_tenants_prod
 *
 * These are ignored in the below statsig segment:
 * - https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/segments/segment%3Aconfluence_-_test_tenants_to_exclude_from_ipm
 *
 * See Slack thread for context: https://atlassian.slack.com/archives/C089VRNRJJF/p1759963056933259?thread_ts=1758160803.383379&cid=C089VRNRJJF
 */

/**
 * Maps a Site object to a URL string.
 *
 * @example
 *
 * ```
 * {
 *   hostname: 'hello.atlassian.net',
 *   cloudId: 'a436116f-02ce-4520-8fbb-7301462a1674',
 * }
 * ```
 *
 * becomes `hello.atlassian.net`
 */
export const urlStringFromSite = (site: Pick<Site, 'hostname'>) =>
  urlStringFromHostname(site.hostname);

export const productFabricSite = {
  hostname: 'product-fabric.atlassian.net',
  cloudId: 'DUMMY-158c8204-ff3b-47c2-adbb-a0906ccc722b',
  confluenceActivationId: '37514d59-a5b9-485c-932f-becb1ce268c1',
  widestUsage: CustomerImpact.ATLASSIAN,
} satisfies Site;

export const helloSite = {
  hostname: 'hello.atlassian.net',
  cloudId: 'a436116f-02ce-4520-8fbb-7301462a1674',
  confluenceActivationId: 'a5c7d427-c980-45d1-a98d-97106e40c71d',
  widestUsage: CustomerImpact.ATLASSIAN,
} satisfies Site;

export const pugSite = {
  hostname: 'pug.jira-dev.com',
  cloudId: 'DUMMY-a5a01d21-1cc3-4f29-9565-f2bb8cd969f5',
  confluenceActivationId: '0591fa3a-8f53-4cfe-8f24-2f7be5d4011b',
  widestUsage: CustomerImpact.ATLASSIAN,
} satisfies Site;

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsManualTestingFreeSiteStaging = {
  hostname: 'atl-fabric-free-stg-hams-site.jira-dev.com',
  cloudId: 'f43c2adf-0510-4e34-876a-e3e30945551e',
  confluenceActivationId: 'eee0f25d-1c1c-4259-a064-3f056a81e233',
  /**
   * We use this one in branch deploys for manual testing
   */
  meantForAutomatedTesting: false,
  unused: true,
} satisfies Site;

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsPaywallFreeSiteStaging = {
  hostname: 'atl-fabric-free-stg-hams-paywall.jira-dev.com',
  cloudId: '7a70aa52-cabe-49ad-a16e-2b6070ee0df0',
  confluenceActivationId: '55fc9550-c412-4cec-8091-8bc846f48077',
  meantForAutomatedTesting: true,
} satisfies Site;

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsFreeSitesStaging = [
  {
    hostname: 'atl-fabric-free-stg-hams-post-2.jira-dev.com',
    cloudId: 'e4716818-0f2f-423a-9890-8e75d16665f5',
    confluenceActivationId: 'f724775e-8c48-4a39-bdb8-4222f9bd1596',
  },
  {
    hostname: 'atl-fabric-free-stg-hams-post-3.jira-dev.com',
    cloudId: '5c1e87de-6dab-466e-af3b-7c2beb3f2a2d',
    confluenceActivationId: 'bbdf3d47-050e-47e2-93c7-f6b8e7fc4279',
  },
  {
    hostname: 'atl-fabric-free-stg-hams-post-4.jira-dev.com',
    cloudId: 'ecdb70ba-c4e7-43e9-adcc-76c04e3a3c23',
    confluenceActivationId: '900465c8-c669-4096-af20-5c269c9ab374',
  },
  {
    hostname: 'atl-fabric-free-stg-hams-post-5.jira-dev.com',
    cloudId: '80351849-5493-45de-adcb-5f90ba13e7c7',
    confluenceActivationId: 'bcb6c40c-530f-4959-aa17-001d1e5f85cf',
  },
  {
    hostname: 'atl-fabric-free-stg-hams.jira-dev.com',
    cloudId: '835d0e64-6a7d-46f3-939f-08159597d05d',
    confluenceActivationId: '1b287ea7-b564-49c2-9e44-ba1cf50a0460',
  },
  ccpMigratedFromHamsManualTestingFreeSiteStaging,
  ccpMigratedFromHamsPaywallFreeSiteStaging,
].map((data) => ({
  meantForAutomatedTesting: true,
  edition: SiteEdition.FREE,
  ...data,
})) satisfies Site[];

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsPaywallFreeSiteProduction = {
  hostname: 'atl-fabric-free-prod-hams-paywall.atlassian.net',
  cloudId: 'e6d0c9dc-4c11-4399-bfb9-f4fd07a83b4d',
  confluenceActivationId: 'b356d88d-4b49-4c29-a408-b459cb3c7531',
  meantForAutomatedTesting: true,
  testConfig: {
    fixedSpace: {
      key: 'FIXEDSPACE',
      id: '167837977',
    },
  },
} satisfies Site;

export const ccpManualTestingFreeSiteStaging: Site = {
  hostname: 'atl-fabric-free-stg-ccp-post-3.jira-dev.com',
  cloudId: 'b8a161ad-9ede-472d-ad36-9edb1cc5c819',
  confluenceActivationId: '9d09fad7-aba7-4309-ae1c-e939f17f6c71',
  /**
   * We use this one in branch deploys for manual testing
   */
  meantForAutomatedTesting: false,
};

export const ccpFreeSitesStaging = (
  [
    ...ccpMigratedFromHamsFreeSitesStaging,
    ccpManualTestingFreeSiteStaging,
    {
      hostname: 'atl-fabric-free-stg.jira-dev.com',
      cloudId: 'TEST--1df64675-832c-466e-b173-c9651ec1e963',
      confluenceActivationId: '8dfe890c-e13b-4fcf-b23b-a5cdd3d34438',
    },
    {
      hostname: 'atl-fabric-free-stg-2.jira-dev.com',
      cloudId: 'TEST--90e39e0e-1c5a-4c5a-9138-1158d8f760e5',
      confluenceActivationId: '4e454eab-795b-49ea-8ce8-d5733c22a9eb',
    },
    {
      hostname: 'atl-fabric-free-stg-3.jira-dev.com',
      cloudId: 'TEST--8fd63f2e-2933-4dc8-a9c2-c858fcb71f3f',
      confluenceActivationId: 'f131566a-12b4-412d-acbc-d29fe8ae6658',
    },
    {
      hostname: 'atl-fabric-free-stg-ccp-post-4.jira-dev.com',
      cloudId: 'df7f7fc9-da5e-49a1-8175-e3566aa09190',
      confluenceActivationId: '2823a845-34b5-467a-9342-a9d3c0292dd3',
    },
    {
      hostname: 'atl-fabric-free-stg-ccp-post-5.jira-dev.com',
      cloudId: '1a5a5ff1-60fa-4205-b603-3e5743501acc',
      confluenceActivationId: 'fee059ed-29e4-419a-bc84-3f0ffb364dea',
    },
    {
      hostname: 'atl-fabric-free-stg-ccp-post-6.jira-dev.com',
      cloudId: '3eaeda37-90a4-4282-961c-7aea764a6866',
      confluenceActivationId: '82368502-fa2c-48c8-8ba0-387a2e2ab104',
    },
  ] satisfies Site[]
).map((data) => ({
  meantForAutomatedTesting: true,
  edition: SiteEdition.FREE,
  ...data,
}));

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsStandardSitesStaging = [
  {
    // Killed by an unknown, not in the audit trail immediate deletion of the Confluence product,
    // and refusal from TDP to reactivate due to a shard mismatch
    hostname: 'atl-fabric-standard-stg-hams-post.jira-dev.com',
    cloudId: '30eb9944-8420-4a4e-bd3c-ee97d342f19a',
    confluenceActivationId: '437fe3f8-5366-462f-914d-32e0c2f5e99a',
    unused: true,
  },
  {
    hostname: 'atl-fabric-standard-stg-hams-post-2.jira-dev.com',
    cloudId: 'f5c36c68-293b-4197-bd90-9bb21cdc8939',
    confluenceActivationId: '4dd70ab4-c621-4dd6-852e-b050c07c1554',
  },
  {
    hostname: 'atl-fabric-standard-stg-hams-post-5.jira-dev.com',
    cloudId: 'e45ef192-f7cf-4b0c-b579-b08d4b1c4e41',
    confluenceActivationId: '00747eb0-abb9-40a6-8051-8540aa8c0fc6',
  },
  {
    hostname: 'atl-fabric-standard-stg-hams-post-6.jira-dev.com',
    cloudId: 'ccb836b1-a467-47c3-9663-78a4096be3df',
    confluenceActivationId: '8695cbad-c54a-4a8f-aca1-a0d25a337c78',
  },
  {
    hostname: 'atl-fabric-standard-stg.jira-dev.com',
    cloudId: 'TEST--ab9aca2c-8791-4101-82f2-c056c09303ba',
    confluenceActivationId: 'bbbf9b53-b66d-38dd-9403-bf396d1eca9c',
  },
  {
    hostname: 'atl-fabric-standard-stg-2.jira-dev.com',
    cloudId: 'TEST--d44431aa-95f3-463c-a8b9-0f3d439298aa',
    confluenceActivationId: 'd073c7b3-8dd1-43ca-822e-68851dc97e27',
  },
  {
    hostname: 'atl-fabric-standard-stg-3.jira-dev.com',
    cloudId: 'TEST--78f2e3a1-2d85-4380-b5e5-e966e572ef9f',
    confluenceActivationId: '7a7fd475-4e57-327e-9970-479d5a50f390',
  },
].map((data) => ({
  meantForAutomatedTesting: true,
  edition: SiteEdition.STANDARD,
  ...data,
})) satisfies Site[];

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsFreeSitesProduction = [
  {
    hostname: 'atl-fabric-free-prod-hams-post-2.atlassian.net',
    cloudId: '03d6d989-78de-4f6e-8e06-1287a4f6dea0',
    confluenceActivationId: '0456fe28-726c-42c3-b0a1-a580f181cd5f',
  },
  {
    hostname: 'atl-fabric-free-prod-hams-post-3.atlassian.net',
    cloudId: '8b003e9c-9cb9-413f-a9ce-b47bf4df1952',
    confluenceActivationId: '97305b66-857c-47d0-b8e5-88263c8c9f25',
  },
  {
    hostname: 'atl-fabric-free-prod-hams-post-4.atlassian.net',
    cloudId: '796eceed-86e2-44b5-b55a-723dc9ffe7a2',
    confluenceActivationId: 'aeb98d55-927f-4467-878d-5fa6c849707d',
  },
  {
    hostname: 'atl-fabric-free-prod-hams-post-5.atlassian.net',
    cloudId: 'ce96d1d5-e8ac-4c3d-a95b-35f4e163c296',
    confluenceActivationId: '85eb3911-9c22-4503-a736-0df261b58c06',
  },
  // TODO: Commented out due to lack of activation ID, may be deleted later
  // {
  //   hostname: 'atl-fabric-free-prod-hams-post.atlassian.net',
  //   cloudId: '589a65b6-1bf2-4441-ab30-73ebe26da1be',
  //   // Suspended and superseded by the post-x sites
  //   unused: true,
  // },
  // {
  //   hostname: 'atl-fabric-free-prod-hams.atlassian.net',
  //   cloudId: '899464ae-5fc9-4509-8e39-405d7fd834e4',
  //   // Suspended and superseded by the post-x sites
  //   unused: true,
  // },
  ccpMigratedFromHamsPaywallFreeSiteProduction,
].map((data) => ({
  meantForAutomatedTesting: true,
  edition: SiteEdition.FREE,
  ...data,
})) satisfies Site[];

export const ccpFreeSitesProduction = [
  {
    hostname: 'atl-fabric-free-prod-ccp-post.atlassian.net',
    cloudId: '679df76d-60ce-4655-851a-595eceba1fa7',
    confluenceActivationId: '2339c488-984b-487a-a7a0-02e1f7c6eca1',
  },
  {
    hostname: 'atl-fabric-free-prod-ccp-post-2.atlassian.net',
    cloudId: '89fbe81f-9856-4456-b6f4-3c7b2869926b',
    confluenceActivationId: '8d653343-0566-48b5-8273-eb8808460e59',
  },
  {
    hostname: 'atl-fabric-free-prod-ccp-post-3.atlassian.net',
    cloudId: '04a5c370-a66a-42fa-9782-b7782cb7a184',
    confluenceActivationId: 'ae6e5d8e-004d-421b-a648-4cb73b38259b',
  },
  {
    hostname: 'atl-fabric-free-prod-ccp-post-4.atlassian.net',
    cloudId: 'f9019ec2-9b70-4cb2-bce3-8e9a711bf1b6',
    confluenceActivationId: '95d9112c-60a1-4390-aa06-050d095032b5',
  },
  ...ccpMigratedFromHamsFreeSitesProduction,
].map((data) => ({
  meantForAutomatedTesting: true,
  edition: SiteEdition.FREE,
  ...data,
})) satisfies Site[];

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsStandardSitesProduction = [
  {
    hostname: 'atl-fabric-standard-prod-hams-post.atlassian.net',
    cloudId: '8367ed66-c82a-45ef-9fa1-b1f7318ba50b',
    confluenceActivationId: '903bf18e-58f7-4c71-b38e-49e7176d40c2',
  },
  {
    hostname: 'atl-fabric-standard-prod-hams-post-2.atlassian.net',
    cloudId: '628b5002-e83b-41d0-a5a5-633462342aaf',
    confluenceActivationId: '37865793-c96f-440f-be46-e4c2884035cb',
  },
  {
    hostname: 'atl-fabric-standard-prod-hams-post-3.atlassian.net',
    cloudId: 'bf9bd1d0-c02a-41b1-8129-773f2b4d12c5',
    confluenceActivationId: 'cf082b47-7ba2-4276-b82e-53bf3feb17a6',
  },
  {
    hostname: 'atl-fabric-standard-prod-hams-post-4.atlassian.net',
    cloudId: 'ffb36fd1-89b4-4f91-88ac-8411b6997d78',
    confluenceActivationId: '1327db2e-ec17-4cac-8e7c-44c477344632',
  },
  {
    hostname: 'atl-fabric-standard-prod-hams.atlassian.net',
    cloudId: 'b58f599c-57fc-4c64-ac3d-8a00e32b4a5e',
    confluenceActivationId: '2d08fc9a-49d6-4e61-a6c6-5635bb665f34',
  },
].map((data) => ({
  meantForAutomatedTesting: true,
  edition: SiteEdition.STANDARD,
  ...data,
})) satisfies Site[];

export const ccpStandardSitesStaging = [
  ...ccpMigratedFromHamsStandardSitesStaging,
] satisfies Site[];

export const ccpStandardSitesProduction = [
  {
    hostname: 'atl-fabric-standard-prod-ccp-post.atlassian.net',
    cloudId: 'ac78637d-e34d-4224-b592-5b6adc1dcb88',
    confluenceActivationId: '982337e9-dfab-49fe-8fbf-591d527f0992',
  },
  {
    hostname: 'atl-fabric-standard-prod-ccp.atlassian.net',
    cloudId: 'fd3f1e69-4df6-411e-a390-f1acb2cc6b0e',
    confluenceActivationId: 'f5ff9ed8-32c1-4bf9-a013-acf528f6bcf8',
  },
  ...ccpMigratedFromHamsStandardSitesProduction,
].map((data) => ({
  meantForAutomatedTesting: true,
  edition: SiteEdition.STANDARD,
  ...data,
})) satisfies Site[];

export const defaultStagingTestingSite = {
  hostname: 'canvas-testing-stg.jira-dev.com',
  cloudId: 'b29aa1b5-ec08-479c-8fd6-b569c8e28ef7',
  confluenceActivationId: '963df9f1-139f-4eb5-aab1-f700f4704a26',
  meantForAutomatedTesting: true,
  testConfig: {
    fixedSpace: {
      key: 'TEXTFIXED',
      id: '229380',
    },
  },
} satisfies Site;

/** NCS-enabled staging tenant for pollinator E2E tests */
export const ncsStagingTestingSite: Site = {
  hostname: 'canvas-ncs-testing-stg.jira-dev.com',
  cloudId: '8cdfc000-3692-4602-8313-3c282ea2b0d1',
  confluenceActivationId: 'c0b32a5e-d45e-4646-9729-72e717d0dc8b',
  meantForAutomatedTesting: true,
  testConfig: {
    fixedSpace: {
      key: 'BOARDFIXED',
      id: '458756',
    },
  },
};

/**
 * Pre-configured backup sites that can be used as overrides for the default
 * staging testing site when the primary site is flaky or unavailable.
 *
 * To add a new backup site:
 * 1. Add the site definition here with full credentials (hostname, cloudId, etc.)
 * 2. Ensure the pollinator bot user has access to the site
 * 3. Ensure the site has been opted out of TAP/IPM messaging (see comments at top of file)
 * 4. Set `DEFAULT_STAGING_SITE_OVERRIDE=<hostname>` in Looper to activate it
 *
 * @see {@link getDefaultStagingTestingSite} for the override resolution logic
 */
export const defaultStagingTestingSiteOverrides: Site[] = [
  // NCS staging site — used by pollinator checks with DEFAULT_STAGING_SITE_OVERRIDE to verify path-based routing.
  ncsStagingTestingSite,
  // Add backup sites here as needed. Example:
  // {
  //   hostname: 'canvas-testing-stg-backup.jira-dev.com',
  //   cloudId: '...',
  //   confluenceActivationId: '...',
  //   meantForAutomatedTesting: true,
  //   testConfig: {
  //     fixedSpace: {
  //       key: 'TEXTFIXED',
  //       id: '...',
  //     },
  //   },
  // },
];

/**
 * Returns the default staging testing site, checking for an override via the
 * `DEFAULT_STAGING_SITE_OVERRIDE` environment variable.
 *
 * When the primary staging site (`canvas-testing-stg.jira-dev.com`) is flaky,
 * ops can set `DEFAULT_STAGING_SITE_OVERRIDE` to the hostname of a backup site
 * from {@link defaultStagingTestingSiteOverrides} to redirect all non-edition
 * pollinator tests to a more stable site.
 *
 * @returns The override site if `DEFAULT_STAGING_SITE_OVERRIDE` is set and matches
 *          a site in the overrides list, otherwise the primary default site.
 * @throws If `DEFAULT_STAGING_SITE_OVERRIDE` is set but doesn't match any site
 *         in the overrides list.
 */
export const getDefaultStagingTestingSite = (): Site => {
  const overrideHostname = process.env.DEFAULT_STAGING_SITE_OVERRIDE;

  if (!overrideHostname) {
    return defaultStagingTestingSite;
  }

  const overrideSite = defaultStagingTestingSiteOverrides.find(
    (site) => site.hostname === overrideHostname,
  );

  if (!overrideSite) {
    const availableHostnames = defaultStagingTestingSiteOverrides.map(
      (site) => site.hostname,
    );

    throw new Error(
      `DEFAULT_STAGING_SITE_OVERRIDE is set to '${overrideHostname}' but no matching site ` +
        `was found in defaultStagingTestingSiteOverrides. ` +
        `Available overrides: [${availableHostnames.join(', ')}]. ` +
        `Either add the site to defaultStagingTestingSiteOverrides in ` +
        `packages/constants/src/environment/sites.ts, or unset the environment variable.`,
    );
  }

  return overrideSite;
};

export const defaultProductionTestingSite = {
  hostname: 'atl-vertigo--shard-conf-prod-us-preprod--canvas-2.atlassian.net',
  cloudId: '6ae5f3c4-235d-461e-b29f-b0fdb7273981',
  confluenceActivationId: 'ee9832cb-4975-4c9e-a09e-8b3b0546c580',
  meantForAutomatedTesting: true,
  testConfig: {
    fixedSpace: {
      key: 'TEXTFIXED',
      id: '655365',
    },
  },
} satisfies Site;

/** NCS-enabled production tenant for pollinator E2E tests */
export const ncsProductionTestingSite: Site = {
  hostname: 'atl-fabric-ncs-testing-prod.atlassian.net',
  cloudId: '0f3f503c-63b0-458e-841d-ea4bdadd5a1e',
  confluenceActivationId: 'b5e1f843-330f-45e6-8b99-3b50755b5eff',
  meantForAutomatedTesting: true,
  testConfig: {
    fixedSpace: {
      key: 'BOARDFIXED',
      id: '985338',
    },
  },
};

/**
 * Pre-configured backup sites that can be used as overrides for the default
 * production testing site.
 *
 * @see {@link getDefaultProductionTestingSite} for the override resolution logic
 */
export const defaultProductionTestingSiteOverrides: Site[] = [
  // NCS production site — used by pollinator checks with DEFAULT_PRODUCTION_SITE_OVERRIDE to verify path-based routing.
  ncsProductionTestingSite,
];

/**
 * Returns the default production testing site, checking for an override via the
 * `DEFAULT_PRODUCTION_SITE_OVERRIDE` environment variable.
 *
 * @returns The override site if `DEFAULT_PRODUCTION_SITE_OVERRIDE` is set and matches
 *          a site in the overrides list, otherwise the primary default site.
 * @throws If `DEFAULT_PRODUCTION_SITE_OVERRIDE` is set but doesn't match any site
 *         in the overrides list.
 */
export const getDefaultProductionTestingSite = (): Site => {
  const overrideHostname = process.env.DEFAULT_PRODUCTION_SITE_OVERRIDE;

  if (!overrideHostname) {
    return defaultProductionTestingSite;
  }

  const overrideSite = defaultProductionTestingSiteOverrides.find(
    (site) => site.hostname === overrideHostname,
  );

  if (!overrideSite) {
    const availableHostnames = defaultProductionTestingSiteOverrides.map(
      (site) => site.hostname,
    );

    throw new Error(
      `DEFAULT_PRODUCTION_SITE_OVERRIDE is set to '${overrideHostname}' but no matching site ` +
        `was found in defaultProductionTestingSiteOverrides. ` +
        `Available overrides: [${availableHostnames.join(', ')}]. ` +
        `Either add the site to defaultProductionTestingSiteOverrides in ` +
        `packages/constants/src/environment/sites.ts, or unset the environment variable.`,
    );
  }

  return overrideSite;
};

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsPremiumSitesStaging = [
  {
    // Consistently getting
    // "{"statusCode":500,"message":"org.springframework.transaction.UnexpectedRollbackException:
    // Transaction rolled back because it has been marked as rollback-only"}" on
    // this site whenever we try create a space
    unused: true,
    hostname: 'atl-fabric-premium-stg-hams.jira-dev.com',
    cloudId: '78fa71b0-1f1d-44f5-aad7-d5ab438eb53e',
    confluenceActivationId: '21cc0f40-4d10-4f4c-a77f-d0d93f4085bc',
  },
  {
    hostname: 'atl-fabric-premium-stg-hams-post.jira-dev.com',
    cloudId: '5ba39301-2ee9-4e60-887b-e363f9b4ae06',
    confluenceActivationId: 'd031edaf-ed8f-48cf-8447-daad7a91b529',
  },
].map((data) => ({
  meantForAutomatedTesting: true,
  edition: SiteEdition.PREMIUM,
  ...data,
})) satisfies Site[];

export const ccpPremiumSitesStaging = [
  ...ccpMigratedFromHamsPremiumSitesStaging,
  defaultStagingTestingSite,
] satisfies Site[];

/**
 * Note: All Confluence products on HAMS have been migrated to CCP!
 * @see https://atlassian.slack.com/archives/C028JJ8LSFJ/p1762389873589029
 */
const ccpMigratedFromHamsPremiumSitesProduction = [
  {
    hostname: 'atl-fabric-premium-prod-hams.atlassian.net',
    cloudId: '07091ff6-51b1-4e2b-b4b7-c62b07655885',
    confluenceActivationId: '37f4b207-0b4c-4adc-bd96-50df32482d55',
  },
  {
    hostname: 'atl-fabric-premium-prod-hams-post.atlassian.net',
    cloudId: '70738083-9588-4515-9561-ee0dc7436a64',
    confluenceActivationId: '4748cff7-a00e-4327-9102-321323487a4f',
  },
].map((data) => ({
  meantForAutomatedTesting: true,
  edition: SiteEdition.PREMIUM,
  ...data,
})) satisfies Site[];

export const ccpPremiumSitesProduction = [
  {
    hostname: 'atl-fabric-premium-prod-ccp.atlassian.net',
    cloudId: '2855216c-8de3-494c-add5-7ad28e25642e',
    confluenceActivationId: '810a5a79-4a9f-47bc-a5de-0c0d0db578b7',
  },
  {
    hostname: 'atl-fabric-premium-prod-ccp-post.atlassian.net',
    cloudId: 'a9faf70f-cf39-4e29-9be0-1a4a9a550218',
    confluenceActivationId: '63a5ae23-f180-4275-8dd9-419e0358c71c',
  },
  defaultProductionTestingSite,
  ...ccpMigratedFromHamsPremiumSitesProduction,
].map((data) => ({
  meantForAutomatedTesting: true,
  edition: SiteEdition.PREMIUM,
  ...data,
})) satisfies Site[];

export const fabricInConfluenceStaging: Site = {
  hostname: 'fabric-in-confluence.jira-dev.com',
  cloudId: 'b5b906eb-2bb0-4c53-b9da-b21685f952ab',
  confluenceActivationId: 'a7a0e579-a6f6-4faa-89ee-5bb133a9eb1b',
  /**
   * We use this one for branch deploy manual testing
   */
  meantForAutomatedTesting: false,
};

export const fabricDevelopmentSite = {
  hostname: 'fabric-development.jira-dev.com',
} satisfies Pick<Site, 'hostname'>;

export const fabricInConfluenceProduction: Site = {
  hostname: 'fabric-in-confluence.atlassian.net',
  cloudId: 'eb0e8137-863c-410e-b012-5a19ad5196a4',
  confluenceActivationId: '8ed728db-b3d4-4c25-acbe-f827c8b81ea1',
  /**
   * We use this one for branch deploy manual testing
   */
  meantForAutomatedTesting: false,
};

export const canvasDevelopmentSite: Site = {
  hostname: 'canvas-development.atlassian.net',
  cloudId: '09b342d9-ac7a-4709-9d9f-80d1d99f36f9',
  confluenceActivationId: 'ed535cfd-6072-409d-a3dc-873ffdbccfe5',
  standalone: true,
};

export const internalSitesWeOwn: Site[] = [
  ...ccpFreeSitesStaging,
  ...ccpStandardSitesStaging,
  ...ccpPremiumSitesStaging,
  ncsStagingTestingSite,

  ...ccpFreeSitesProduction,
  ...ccpStandardSitesProduction,
  ...ccpPremiumSitesProduction,
  ncsProductionTestingSite,

  // Access has been removed
  canvasDevelopmentSite,
  {
    hostname: 'atl-vertigo--shard-conf-prod-us-preprod--canvas.atlassian.net',
    cloudId: '1500ee6b-f234-41e1-b52c-ec3b0f4819f6',
    confluenceActivationId: 'd4eabcca-b944-401a-a84f-e8e0a789f6de',
    meantForAutomatedTesting: true,
  },
  fabricInConfluenceProduction,
  fabricInConfluenceStaging,
  {
    hostname: 'fabric-dare.jira-dev.com',
    cloudId: 'b817aaf3-da8a-49a5-aa26-609e4ff9621b',
    confluenceActivationId: '13ba05f0-e2e4-4bde-9583-dc148750b2bb',
  },
  {
    hostname: 'fabric-restrictions.jira-dev.com',
    cloudId: 'f7fcdc63-70d1-4f9a-8987-00242c92f260',
    confluenceActivationId: '1a9a3c60-e784-4f57-8aec-6f3f1ca8bf95',
    // Failing at the moment so marked as standalone to avoid HTTP errors
    standalone: true,
  },
  // TODO: Commented out due to lack of activation ID, may be deleted later
  // {
  //   hostname: 'canvas.dev.atlassian.com',
  //   cloudId: '382e7407-d3b8-4b14-970b-d209d87ee26c',
  //   confluenceActivationId: '',
  //   standalone: true,
  // },
];

/**
 * Environment Validation mints a seed-data site per workflow run, so these cannot be
 * enumerated in {@link internalSitesWeOwn} ahead of time.
 *
 * Both anchors matter. Without them a host that merely contains the shape would be
 * accepted — `evil.q0m-ev-seed-data-site-confluence-site-prem.atlassian.net`, or the
 * shape followed by a foreign domain.
 */
const EV_SEED_SITE_HOSTNAME_PATTERN =
  /^[a-z0-9]+-ev-seed-data-site-confluence-site-[a-z0-9]+\.(?:atlassian\.net|jira-dev\.com)$/i;

/**
 * Tracked sites must match on both hostname and cloudId. A correct cloudId alone would
 * otherwise vouch for an arbitrary hostname, and such tooling addresses every request by
 * hostname.
 *
 * The Environment Validation branch matches on hostname only, since its cloudId is not
 * knowable ahead of time. Residual risk: `*.atlassian.net` subdomains are self-service,
 * so a site could be deliberately named into the shape. Nobody reaches it by typo — the
 * literal is 35 characters — so this is accepted rather than mitigated.
 */
export const isInternalTestSite = (hostname: string, cloudId: string): boolean =>
  internalSitesWeOwn.some(
    (site) => site.cloudId === cloudId && site.hostname === hostname,
  ) || EV_SEED_SITE_HOSTNAME_PATTERN.test(hostname);
